import { useEffect, useState } from 'react';

export const CONSENT_KEY = 'cookie_consent_v3';
export const CONSENT_VERSION = 3;
export const CONSENT_CHANGE_EVENT = 'easyfuneral:consent-changed';
export const COOKIE_PREFERENCES_EVENT = 'easyfuneral:open-cookie-preferences';
let volatileConsent = null;

function expiryFor(timestamp) {
    const expiry = new Date(timestamp);
    expiry.setUTCMonth(expiry.getUTCMonth() + 6);
    return expiry.getTime();
}

export function saveConsent({ externalServices = false } = {}) {
    const timestamp = Date.now();
    const payload = {
        version: CONSENT_VERSION,
        necessary: true,
        externalServices: externalServices === true,
        timestamp,
        expiresAt: expiryFor(timestamp),
    };
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
        volatileConsent = null;
        localStorage.removeItem('cookie_consent_v1');
        localStorage.removeItem('cookie_consent_v2');
    } catch {
        // Keep the choice for this page when browser storage is unavailable.
        volatileConsent = payload;
    }
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: payload }));
    return payload;
}

export function getConsent() {
    if (volatileConsent) return volatileConsent;
    try {
        const stored = localStorage.getItem(CONSENT_KEY);
        return stored ? JSON.parse(stored) : volatileConsent;
    } catch {
        return volatileConsent;
    }
}

export function isConsentValid(consent) {
    if (!consent || consent.version !== CONSENT_VERSION || consent.necessary !== true
        || typeof consent.externalServices !== 'boolean'
        || !Number.isFinite(consent.timestamp) || !Number.isFinite(consent.expiresAt)) return false;
    const now = Date.now();
    return consent.timestamp <= now && consent.expiresAt > consent.timestamp
        && consent.expiresAt <= expiryFor(consent.timestamp) && now < consent.expiresAt;
}

export function hasConsent(category) {
    if (category !== 'externalServices') return false;
    const consent = getConsent();
    return isConsentValid(consent) && consent[category] === true;
}

export function openCookiePreferences() {
    window.dispatchEvent(new Event(COOKIE_PREFERENCES_EVENT));
}

export function useCookieConsent() {
    const [consent, setConsent] = useState(getConsent);

    useEffect(() => {
        const refresh = () => setConsent(getConsent());
        const onStorage = event => {
            if (event.key === CONSENT_KEY || event.key === null) {
                volatileConsent = null;
                refresh();
            }
        };
        window.addEventListener(CONSENT_CHANGE_EVENT, refresh);
        window.addEventListener('storage', onStorage);
        refresh();
        return () => {
            window.removeEventListener(CONSENT_CHANGE_EVENT, refresh);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    useEffect(() => {
        if (!isConsentValid(consent)) return undefined;
        // Browsers cap timers at roughly 24 days. Recheck long-lived choices.
        const timeout = window.setTimeout(() => {
            const latest = getConsent();
            setConsent(latest ? { ...latest } : null);
        }, Math.min(consent.expiresAt - Date.now() + 1, 2147483647));
        return () => window.clearTimeout(timeout);
    }, [consent]);

    const valid = isConsentValid(consent);
    return {
        consent,
        externalServices: valid && consent.externalServices,
    };
}
